import { ApiUrlConstants } from '../../common/constants/api.constants';
import { CommonService } from '../../shared/services/common.service';
import { Injectable, OnInit } from '@angular/core';
import { CacheService } from '../../shared/services/cache.service';
import { Observable } from 'rxjs/Observable';
import { catchError } from 'rxjs/internal/operators/catchError';
import { CacheKey } from '../../shared/constants/cachekey.constants';
import { StoreConfig } from '../../shared/interfaces/store-config.interface';
import { Product } from '../../products/interfaces/product.interface';
import { Quickview } from '../../products/interfaces/product-quickview.interface';
import { ActiveSessionService } from '../../shared/services/active-session.service';
import { HttpClient } from '@angular/common/http';
import { throwError } from 'rxjs/internal/observable/throwError';
import { Subject } from 'rxjs/Subject';
import { Member } from '../../shared/interfaces/member.interface';
import { ConfigurationService } from 'src/app/shared/services/configuration.service';
import { ReportOnlyCountryService } from 'src/app/shared/services/report-only-country.service';

@Injectable()
export class ProductService implements OnInit {
  // Observable bollean sources
  private sideNavOpenSource = new Subject<boolean>();
  private sideNavCloseSource = new Subject<boolean>();

  // Observable bollean streams
  sideNavOpen$ = this.sideNavOpenSource.asObservable();
  sideNavClose$ = this.sideNavCloseSource.asObservable();

  isoCountryCode: string;
  userInfo: Member;
  store: StoreConfig;
  constructor(
    private _http: HttpClient,
    private _cacheService: CacheService,
    private _commonService: CommonService,
    private _activeSessionService: ActiveSessionService,
    private configurationService: ConfigurationService,
    private _reportOnlyCountryService: ReportOnlyCountryService
  ) {
    this.store = this._cacheService.get(CacheKey.StoreConfiguration);
    if (this.store) {
      this.isoCountryCode = this.store.homeIsocodeThree;
    }
    this.userInfo = this._cacheService.get(CacheKey.UserInfo);

  }
  ngOnInit() {
  }

  /**
   * To get all products from the service
   * @param  {number} storeId
   * @returns Observable   */
  getFeaturedProducts(storeId: number, languageCode: string, pageSize: number, pageNumber: number, sortBy: string): Observable<Product[]> {
    const memberTitleId = this._activeSessionService.getMemberTitleIdForProducts();
    languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
    const purchaseFlowType = this._commonService.getPurchaseFlowType();
    const wholesaleQualified: boolean = this.userInfo ? this.userInfo.transactionalWholesaleQualified : false;
    let _featuredUrl: string = ApiUrlConstants.productApiUrl + '/v1/stores/' + storeId + '/products/featured?languageCode='
      + languageCode + '&pageSize=' + pageSize + '&pageNumber=' + pageNumber + '&sortBy=' + sortBy
      + '&memberTitleId=' + memberTitleId + '&purchaseFlowType=' + purchaseFlowType + '&countryCode='
      + this.isoCountryCode + '&wholesaleQualified=' + wholesaleQualified
      + '&optin=' + (this.userInfo ? this.userInfo.transactionalOptin : false)
      + '&taxInclusive=' + this.store.taxInclusive;
    const priceTierId = this._commonService.getMemberSalePromotionPriceTierId();
    if (priceTierId) {
      _featuredUrl += '&priceTierId=' + priceTierId;
    }
    return this._http
      .get<Product[]>(_featuredUrl)
      .pipe(catchError(this.handleErrorObservable));
  }

  /**
  * get Recommended Products
  * @param  {number} storeId
  * @param  {number} productId
  * @param  {number} groupId
  * @param  {string} languageCode
  * @param  {number} pageSize
  * @param  {number} pageNumber
  * @param  {string} sortBy  * @returns Observable
  */
  getRecommendedProducts(storeId: number, productId: number, groupId: number,
    languageCode: string, pageSize: number, pageNumber: number, sortBy: string): Observable<Product[]> {
    const memberTitleId = this._activeSessionService.getMemberTitleIdForProducts();
    languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
    const purchaseFlowType = this._commonService.getPurchaseFlowType();
    const wholesaleQualified: boolean = this.userInfo ? this.userInfo.transactionalWholesaleQualified : false;
    let _recommendedUrl: string = ApiUrlConstants.productApiUrl + '/v1/stores/' + storeId + '/products/' +
      productId + '/group/' + groupId + '?languageCode=' + languageCode + '&pageSize=' +
      pageSize + '&pageNumber=' + pageNumber + '&sortBy=' + sortBy + '&memberTitleId=' +
      memberTitleId + '&purchaseFlowType=' + purchaseFlowType +
      '&countryCode=' + this.isoCountryCode + '&wholesaleQualified=' + wholesaleQualified
      + '&optin=' + (this.userInfo ? this.userInfo.transactionalOptin : false)
      + '&taxInclusive=' + this.store.taxInclusive;
    const priceTierId = this._commonService.getMemberSalePromotionPriceTierId();
    if (priceTierId) {
      _recommendedUrl += '&priceTierId=' + priceTierId;
    }
    return this._http
      .get<Product[]>(_recommendedUrl)
      .pipe(catchError(this.handleErrorObservable));
  }

  /**
   * to get product details with its id (in quick view)
   * @param  {number} productId
   * @param  {number} storeId
   * @returns Observable
   */
  getProductById(productId: number, storeId: number, languageCode: string): Observable<Quickview> {
    const memberTitleId = this._activeSessionService.getMemberTitleIdForProducts();
    languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
    const purchaseFlowType = this._commonService.getPurchaseFlowType();
    const wholesaleQualified: boolean = this.userInfo ? this.userInfo.transactionalWholesaleQualified : false;
    let _url: string = ApiUrlConstants.productApiUrl + '/v1/stores/' + storeId + '/products/' + productId + '/detail?languageCode=' +
      languageCode + '&memberTitleId=' + memberTitleId + '&purchaseFlowType=' + purchaseFlowType
      + '&countryCode=' + this.isoCountryCode + '&wholesaleQualified=' + wholesaleQualified
      + '&optin=' + (this.userInfo ? this.userInfo.transactionalOptin : false);
    const priceTierId = this._commonService.getMemberSalePromotionPriceTierId();
    if (priceTierId) {
      _url += '&priceTierId=' + priceTierId;
    }
    return this._http
      .get<Quickview>(decodeURIComponent(encodeURIComponent(_url)))
      .pipe(catchError(this.handleErrorObservable));
  }

  /**
   * to get proudct by its category id
   * @param  {number} categoryId
   * @param  {number} storeId
   * @returns Observable
   */
  getProductByCategoryId(categoryId: number, storeId: number, languageCode: string,
    pageSize: number, pageNumber: number, sortBy: string): Observable<Product[]> {
    if (categoryId) {
    const memberTitleId = this._activeSessionService.getMemberTitleIdForProducts();
    languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
    const purchaseFlowType = this._commonService.getPurchaseFlowType();
    const wholesaleQualified: boolean = this.userInfo ? this.userInfo.transactionalWholesaleQualified : false;
    let _productsUrl: string = ApiUrlConstants.productApiUrl + '/v1/stores/' + storeId + '/categories/' +
      categoryId + '/products?languageCode=' + languageCode + '&pageSize=' + pageSize + '&pageNumber=' +
      pageNumber + '&sortBy=' + sortBy + '&memberTitleId=' + memberTitleId + '&purchaseFlowType=' +
      purchaseFlowType + '&countryCode=' + this.isoCountryCode + '&wholesaleQualified=' + wholesaleQualified
      + '&optin=' + (this.userInfo ? this.userInfo.transactionalOptin : false)
      + '&taxInclusive=' + this.store.taxInclusive;
    const priceTierId = this._commonService.getMemberSalePromotionPriceTierId();
    if (priceTierId) {
      _productsUrl += '&priceTierId=' + priceTierId;
    }
    return this._http
      .get<Product[]>(_productsUrl)
      .pipe(catchError(this.handleErrorObservable));
    }  else {
      return Observable.throw('category id is empty');
    }
  }

  /**
   * Service message commands
   * @param  {boolean} status
   */
  sideNavOpen(status: boolean) {
    this.sideNavOpenSource.next(status);
  }

  sideNavClose(status: boolean) {
    this.sideNavCloseSource.next(status);
  }

  /**
* @param {number} storeId
* @returns Observable
*/
  getCustomCatalogs(storeId: number): Observable<any[]> {
    if (this._reportOnlyCountryService.reportOnlyCountry(this.isoCountryCode) || this.titanPlatformEnabled() === false) {
      return Observable.of([]);
    }
    const memberTitleId = this._activeSessionService.getMemberTitleIdForProducts();
    const purchaseFlowType = this._commonService.getPurchaseFlowType();
    const wholesaleQualified: boolean = this.userInfo ? this.userInfo.transactionalWholesaleQualified : false;
    const _customCatalogUrl: string = ApiUrlConstants.productApiUrl + '/v1/stores/' + storeId +
      '/customcatalogs' + '?countryCode=' + this.isoCountryCode + '&memberTitleId=' + memberTitleId
      + '&purchaseFlowType=' + purchaseFlowType
      + '&languageCode=' + this._cacheService.getCookieValue(CacheKey.languageCode) + '&wholesaleQualified=' + wholesaleQualified
      + '&optin=' + (this.userInfo ? this.userInfo.transactionalOptin : false)
      + '&taxInclusive=' + this.store.taxInclusive;
    return this._http.get<any[]>(_customCatalogUrl)
      .pipe(catchError(this.handleErrorObservable));
  }

  private titanPlatformEnabled(): boolean {
    let titanPlatformEnabled = true;
    //Check whether 'titanPlatformEnabled' exists in the cache or not
    if ('titanPlatformEnabled' in this.store) {
      titanPlatformEnabled = (this.store as any).titanPlatformEnabled;
    }
    return titanPlatformEnabled;
  }

  /**
* @param {number} storeId
* @returns Observable
*/
  getProductsByCatalogId(storeId: number, catalogId: number,
    languageCode: string, pageSize: number, pageNumber: number, sortBy: string): Observable<Product[]> {
    if (this._reportOnlyCountryService.reportOnlyCountry(this.isoCountryCode)) {
      return Observable.of([]);
    }
    const memberTitleId = this._activeSessionService.getMemberTitleIdForProducts();
    languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
    const purchaseFlowType = this._commonService.getPurchaseFlowType();
    const wholesaleQualified: boolean = this.userInfo ? !!(this.userInfo.transactionalWholesaleQualified) : false;
    const optin: boolean = this.userInfo ? !!(this.userInfo.transactionalOptin) : false;
    const taxInclusive: boolean = !!(this.store && this.store.taxInclusive);
    let _productsUrl: string = ApiUrlConstants.productApiUrl + '/v1/stores/' + storeId +
      '/customcatalogs/' + catalogId + '?languageCode=' + languageCode + '&pageSize=' + pageSize +
      '&pageNumber=' + pageNumber + '&sortBy=' + sortBy + '&memberTitleId=' + memberTitleId +
      '&purchaseFlowType=' + purchaseFlowType + '&countryCode=' + this.isoCountryCode + '&wholesaleQualified=' + wholesaleQualified
      + '&optin=' + optin
      + '&taxInclusive=' + taxInclusive;
    const priceTierId = this._commonService.getMemberSalePromotionPriceTierId();
    if (priceTierId) {
      _productsUrl += '&priceTierId=' + priceTierId;
    }
    return this._http.get<Product[]>(decodeURIComponent(encodeURIComponent(_productsUrl)))
      .pipe(catchError(this.handleErrorObservable));
  }


  /**
   * To handle the obervable error response
   * @param err 
   */
  private handleErrorObservable(err) {
    // in a real world app, we may send the server to some remote logging infrastructure
    // instead of just logging it to the console
    let errorMessage: string;
    if (err.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      errorMessage = `An error occurred: ${err.error.message}`;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      errorMessage = `Backend returned code ${err.status}: ${err.error}`;
    }
    console.error(err);
    return throwError(errorMessage);
  }

}
